import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {
    Currency,
    FullOfferUi,
    OfferInListDTO,
    OfferType,
    SubtractionType,
    UpdateOfferRequest
} from "../../../api/generated";
import {offerApi, RequestStatus} from "../../../api/apiConfiguration";
import {selectExceptionally} from "../../../utils/selectExceptionally";

export const fetchOfferToUpdate = createAsyncThunk(
    'offerUpdate/fetchOfferById',
    (publicId: string) => offerApi.getOfferByPublicId({publicId})
);

export const updateOffer = createAsyncThunk<OfferInListDTO, undefined, { state: RootState }>(
    "offerUpdate/update",
    (_payload, {getState}) => {
        const {offerUpdate} = getState();
        const {updateOfferRequest} = offerUpdate;
        const request = mapRequest(updateOfferRequest);
        return offerApi.updateOffer({updateOfferRequest: request});
    }
);

export type UpdateOfferRequestPartial = Partial<UpdateOfferRequest>

export interface UpdateOfferState {
    updateOfferRequest?: UpdateOfferRequestPartial
    initialOfferLoadingStatus: RequestStatus
    updateOfferRequestStatus: RequestStatus
}

const buildInitialState = (): UpdateOfferState => {
    return {
        initialOfferLoadingStatus: RequestStatus.NONE,
        updateOfferRequestStatus: RequestStatus.NONE,
    }
}

const initialState: UpdateOfferState = buildInitialState()

export const offerUpdateSlice = createSlice({
    name: 'offerUpdate',
    initialState,
    reducers: {
        clearUpdateOfferState: () => {
            return buildInitialState();
        },
        updateUpdateOfferRequest: (state, {payload}: PayloadAction<UpdateOfferRequestPartial>) => {
            if (payload.offerType && payload.offerType === OfferType.NewVehicle && !state.updateOfferRequest?.currency) {
                payload = {
                    ...payload,
                    currency: Currency.Gbp
                }
            }
            if (payload.offerType && !state.updateOfferRequest?.discount) {
                state.updateOfferRequest = {
                    ...state.updateOfferRequest,
                    discount: {
                        id: undefined,
                        subtractionValue: 0,
                        subtractionType: SubtractionType.Number,
                    }
                }
            }

            state.updateOfferRequest = {
                ...state.updateOfferRequest,
                ...payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchOfferToUpdate.pending, (state) => {
            state.initialOfferLoadingStatus = RequestStatus.PENDING;
        })
        builder.addCase(fetchOfferToUpdate.fulfilled, (state, {payload}) => {
            state.initialOfferLoadingStatus = RequestStatus.RESOLVED;
            state.updateOfferRequest = assignFields(payload);
        })
        builder.addCase(fetchOfferToUpdate.rejected, (state) => {
            state.initialOfferLoadingStatus = RequestStatus.REJECTED;
        })
        builder.addCase(updateOffer.pending, (state) => {
            state.updateOfferRequestStatus = RequestStatus.PENDING;
        })
        builder.addCase(updateOffer.fulfilled, (state) => {
            state.updateOfferRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(updateOffer.rejected, (state) => {
            state.updateOfferRequestStatus = RequestStatus.REJECTED;
        })
    },
});


const assignFields = (offerUi: FullOfferUi): UpdateOfferRequestPartial => {
    return {
        id: offerUi.id,
        publicId: offerUi.publicId,
        publicBotId: offerUi.publicBotId,
        publicLocationId: offerUi.publicLocationId,
        offerStatus: offerUi.offerStatus,
        archivedAt: offerUi.archivedAt,
        offerType: offerUi.offerType,
        offerSource: offerUi.offerSource,
        offerSourceName: offerUi.offerSourceName,
        title: offerUi.title,
        description: offerUi.description,
        make: offerUi.make,
        model: offerUi.model,
        startDate: offerUi.startDate,
        endDate: offerUi.endDate,
        redirectUrl: offerUi.redirectUrl,
        imageUrl: offerUi.imageUrl,
        discount: offerUi.discount,
        currency: offerUi.currency,
        financeProduct: offerUi.financeProduct,
        cashPrice: offerUi.cashPrice,
        monthlyPayments: offerUi.monthlyPayments,
        customerDeposit: offerUi.customerDeposit,
        manufacturerDepositContribution: offerUi.manufacturerDepositContribution,
        totalDeposit: offerUi.totalDeposit,
        dealerContribution: offerUi.dealerContribution,
        balanceToFinance: offerUi.balanceToFinance,
        totalAmountPayable: offerUi.totalAmountPayable,
        totalChargeForCredit: offerUi.totalChargeForCredit,
        optionalFinalPayment: offerUi.optionalFinalPayment,
        durationOfAgreementInMonths: offerUi.durationOfAgreementInMonths,
        apr: offerUi.apr,
        fixedInterestRate: offerUi.fixedInterestRate,
        mileagePerAnnum: offerUi.mileagePerAnnum,
        excessMileageCharge: offerUi.excessMileageCharge,
    }
}

const mapRequest = (request?: UpdateOfferRequestPartial): UpdateOfferRequest => {
    if (request) {
        return {
            id: selectExceptionally(request.id),
            publicId: selectExceptionally(request.publicId),
            publicBotId: request.publicBotId,
            publicLocationId: request.publicLocationId,
            offerStatus: selectExceptionally(request.offerStatus),
            archivedAt: request.archivedAt,
            offerType: selectExceptionally(request.offerType),
            offerSource: selectExceptionally(request.offerSource),
            offerSourceName: selectExceptionally(request.offerSourceName),
            title: selectExceptionally(request.title),
            description: selectExceptionally(request.description),
            make: selectExceptionally(request.make),
            model: selectExceptionally(request.model),
            startDate: selectExceptionally(request.startDate),
            endDate: selectExceptionally(request.endDate),
            redirectUrl: selectExceptionally(request.redirectUrl),
            imageUrl: selectExceptionally(request.imageUrl),
            discount: request.discount,
            currency: request.currency,
            financeProduct: request.financeProduct,
            cashPrice: request.cashPrice,
            monthlyPayments: request.monthlyPayments,
            customerDeposit: request.customerDeposit,
            manufacturerDepositContribution: request.manufacturerDepositContribution,
            totalDeposit: request.totalDeposit,
            dealerContribution: request.dealerContribution,
            balanceToFinance: request.balanceToFinance,
            totalAmountPayable: request.totalAmountPayable,
            totalChargeForCredit: request.totalChargeForCredit,
            optionalFinalPayment: request.optionalFinalPayment,
            durationOfAgreementInMonths: request.durationOfAgreementInMonths,
            apr: request.apr,
            fixedInterestRate: request.fixedInterestRate,
            mileagePerAnnum: request.mileagePerAnnum,
            excessMileageCharge: request.excessMileageCharge
        } as UpdateOfferRequest
    } else {
        throw new Error("update offer request not initialised")
    }
}

export const {
    clearUpdateOfferState,
    updateUpdateOfferRequest
} = offerUpdateSlice.actions;

export const updateOfferRequestSelector = (state: RootState) => state.offerUpdate.updateOfferRequest as UpdateOfferRequestPartial;
export const initialOfferLoadingStatusSelector = (state: RootState): RequestStatus => state.offerUpdate.initialOfferLoadingStatus;
export const updateOfferRequestStatusSelector = (state: RootState): RequestStatus => state.offerUpdate.updateOfferRequestStatus;
